import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { BiArrowBack, BiCheck, BiPlus, BiX } from 'react-icons/bi';
import { platformAPI } from '../../../services/platformAPI';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import dhruvLogo from '../../../assets/dhruv.png';
import { clearOrganisationSettings } from '../../../utils/organisationSettings';
import { setWorkspaceDateFormat } from '../../../utils/workspaceDates';
import { applyBrandColours } from '../../../utils/brandColours';
import { settingsBase } from './settingsPaths';
import '../tender/CustomerMaster.scss';
import './OrganisationSettingsPage.scss';
import TurnoverSection from './TurnoverSection';
import WorkPackageSettings from './WorkPackageSettings';

const Field = ({ label, wide, children }) => (
  <Form.Group className={`cust-field${wide ? ` cust-field--${wide}` : ''}`}>
    <Form.Label>{label}</Form.Label>
    {children}
  </Form.Group>
);

const CurrencyBoard = ({ title, note, codes, selected, mode, current, onToggle, onChoose }) => {
  const onCount = mode === 'default' ? (current ? 1 : 0) : selected.length;
  return (
    <article className="org-values__card">
      <header className="org-values__head">
        <div>
          <h3>{title}</h3>
          <p>{note}</p>
        </div>
        <span className="org-values__count" aria-label={`${onCount} selected`}>{String(onCount).padStart(2, '0')}</span>
      </header>
      <ol className="org-values__list">
        {codes.map((row) => {
          const on = mode === 'default' ? row.code === current : selected.includes(row.code);
          return (
            <li key={row.code}>
              <button
                type="button"
                className={`org-money__row${on ? ' is-on' : ''}`}
                aria-pressed={on}
                onClick={() => (mode === 'default' ? onChoose(row.code) : onToggle(row.code))}
              >
                <span className="org-values__index">{row.code}</span>
                <span className="org-values__label">{row.label}</span>
                <span className="org-money__mark">{on ? (mode === 'default' ? 'Default' : 'On') : 'Off'}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </article>
  );
};

const ChoiceBoard = ({ title, note, options, current, onChoose }) => (
  <article className="org-values__card">
    <header className="org-values__head">
      <div>
        <h3>{title}</h3>
        <p>{note}</p>
      </div>
      <span className="org-values__count">01</span>
    </header>
    <ol className="org-values__list">
      {options.map((row, index) => {
        const on = String(row.code) === String(current);
        return (
          <li key={row.code}>
            <button
              type="button"
              className={`org-money__row${on ? ' is-on' : ''}`}
              aria-pressed={on}
              onClick={() => onChoose(row.code)}
            >
              <span className="org-values__index">{String(index + 1).padStart(2, '0')}</span>
              <span className="org-values__label">{row.label}</span>
              <span className="org-money__mark">{on ? 'Set' : 'Off'}</span>
            </button>
          </li>
        );
      })}
    </ol>
  </article>
);

const RuleCard = ({ title, note, value, min, max, unit, onChange }) => (
  <article className="org-values__card">
    <header className="org-values__head">
      <div>
        <h3>{title}</h3>
        <p>{note}</p>
      </div>
      <span className="org-values__count">{value}</span>
    </header>
    <div className="org-values__add">
      <input
        type="number"
        min={min}
        max={max}
        step="1"
        value={value}
        aria-label={title}
        onChange={(event) => onChange(event.target.value)}
      />
      <span className="org-rule__unit">{unit}</span>
    </div>
  </article>
);

const PersonnelBoard = ({ types, onChange }) => {
  const [draft, setDraft] = useState('');
  const items = types || [];
  const full = items.length >= 12;
  const commit = () => {
    const label = draft.trim().replace(/\s+/g, ' ').slice(0, 40);
    if (!label || full) return;
    onChange([...items, { code: '', label }]);
    setDraft('');
  };
  return (
    <article className="org-values__card org-span">
      <header className="org-values__head">
        <div>
          <h3>Roster</h3>
          <p>A person already saved keeps their type if it is later removed from this list.</p>
        </div>
        <span className="org-values__count">{String(items.length).padStart(2, '0')}</span>
      </header>
      <ol className="org-values__list">
        {items.map((row, index) => (
          <li key={`${row.code || 'new'}-${index}`}>
            <span className="org-values__index">{String(index + 1).padStart(2, '0')}</span>
            <input
              className="org-type__label"
              value={row.label}
              aria-label={`Personnel type ${index + 1}`}
              maxLength={40}
              onChange={(event) => onChange(items.map((item, itemIndex) => (
                itemIndex === index ? { ...item, label: event.target.value } : item
              )))}
            />
            <button
              type="button"
              className="org-values__remove"
              aria-label={`Remove ${row.label || 'type'}`}
              disabled={items.length < 2}
              onClick={() => onChange(items.filter((_, itemIndex) => itemIndex !== index))}
            >
              <BiX />
            </button>
          </li>
        ))}
      </ol>
      <div className="org-values__add">
        <input
          value={draft}
          maxLength={40}
          placeholder={full ? 'This list is full' : 'Add a type'}
          aria-label="Add a personnel type"
          disabled={full}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commit();
            }
          }}
        />
        <button type="button" onClick={commit} disabled={full || !draft.trim()}>
          <BiPlus />
          Add
        </button>
      </div>
    </article>
  );
};

const IdentityCard = ({ title, note, value, max, multiline, wide, onChange }) => (
  <article className={`org-values__card${wide ? ' org-span' : ''}`}>
    <header className="org-values__head">
      <div>
        <h3>{title}</h3>
        <p>{note}</p>
      </div>
    </header>
    <div className="org-values__add">
      {multiline ? (
        <textarea rows="4" maxLength={max} value={value || ''} aria-label={title} onChange={(event) => onChange(event.target.value)} />
      ) : (
        <input maxLength={max} value={value || ''} aria-label={title} onChange={(event) => onChange(event.target.value)} />
      )}
    </div>
  </article>
);

const ColourCard = ({ primary, secondary, onChange }) => (
  <article className="org-values__card">
    <header className="org-values__head">
      <div>
        <h3>Colours</h3>
        <p>Accent and charcoal. The accent is used for buttons and links. Charcoal is the dark text.</p>
      </div>
    </header>
    <div className="org-colours">
      {[['primaryColor', 'Accent', primary], ['secondaryColor', 'Charcoal', secondary]].map(([name, label, value]) => (
        <label key={name} className="org-colours__row">
          <input
            type="color"
            aria-label={label}
            value={/^#[0-9A-Fa-f]{6}$/.test(value || '') ? value : '#a63726'}
            onChange={(event) => onChange(name, event.target.value)}
          />
          <span>{label}</span>
          <input maxLength="7" value={value || ''} onChange={(event) => onChange(name, event.target.value)} />
        </label>
      ))}
    </div>
  </article>
);

const LogoCard = ({ logoUrl, onChange }) => {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const choose = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setBusy(true);
    setNote('');
    try {
      const response = await platformAPI.uploadOrganisationLogo(file);
      onChange(response.data?.data?.settings?.workspace?.logoUrl || '');
      setNote('Logo saved.');
    } catch (error) {
      setNote(error.response?.data?.message || 'The logo could not be saved.');
    } finally {
      setBusy(false);
    }
  };
  const clear = async () => {
    setBusy(true);
    setNote('');
    try {
      await platformAPI.clearOrganisationLogo();
      onChange('');
      setNote('Logo removed. The built-in mark is used.');
    } catch (error) {
      setNote(error.response?.data?.message || 'The logo could not be removed.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <article className="org-values__card">
      <header className="org-values__head">
        <div>
          <h3>Logo</h3>
          <p>PNG, JPG, or WEBP, up to 2 MB. It is shown on the login page and in the shell.</p>
        </div>
      </header>
      <div className="org-logo">
        <img src={logoUrl || dhruvLogo} alt="" />
        <label className={busy ? 'is-busy' : ''}>
          {busy ? 'Saving…' : 'Choose file'}
          <input type="file" accept="image/png,image/jpeg,image/webp" onChange={choose} disabled={busy} />
        </label>
        {logoUrl ? <button type="button" onClick={clear} disabled={busy}>Remove</button> : null}
        {note ? <p>{note}</p> : null}
      </div>
    </article>
  );
};

const IMPORT_BOARDS = [
  ['customerCategories', 'Clients', 'Active values appear on customer forms and imports, in this order.'],
  ['serviceCategories', 'Services', 'Active values appear on service forms and imports, in this order.'],
  ['serviceBilling', 'Billing', 'Active billing bases on service forms and imports. A blank import cell uses the first active value.'],
  ['sectorGroups', 'Sectors', 'Active groups on sector forms and imports, in this order.'],
  ['tenderTypeGroups', 'Tender types', 'Active groups on tender type forms and imports, in this order.'],
  ['sourceGroups', 'Sources', 'Active groups on source forms and imports, in this order.'],
  ['credentialCategories', 'Credentials', 'Active categories on knowledge credential forms, in this order.'],
  ['eligibilityCategories', 'Eligibility', 'Active categories on tender eligibility requirements, in this order.'],
  ['paymentModes', 'Payment modes', 'Active fee/EMD payment modes. Labels become codes (Demand draft → demand_draft).'],
  ['staffingSources', 'Staffing', 'Active personnel sources on tender role slots (Employee, Associate, Candidate, JV nominee).']
];

const normalizeValues = (list) => {
  const seen = new Set();
  const out = [];
  (Array.isArray(list) ? list : []).forEach((item) => {
    let value = '';
    let active = true;
    if (item && typeof item === 'object') {
      value = String(item.value ?? item.label ?? '').trim().replace(/\s+/g, ' ').slice(0, 60);
      active = item.active !== false && item.isActive !== false;
    } else {
      value = String(item || '').trim().replace(/\s+/g, ' ').slice(0, 60);
    }
    const key = value.toLowerCase();
    if (!value || seen.has(key)) return;
    seen.add(key);
    out.push({ value, active });
  });
  return out;
};

const ValueBoard = ({ title, note, values, onChange }) => {
  const [draft, setDraft] = useState('');
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const items = normalizeValues(values);
  const activeCount = items.filter((item) => item.active).length;
  const full = items.length >= 24;

  const commit = () => {
    const label = draft.trim().replace(/\s+/g, ' ').slice(0, 60);
    if (!label || full) return;
    if (items.some((item) => item.value.toLowerCase() === label.toLowerCase())) {
      setDraft('');
      return;
    }
    onChange([...items, { value: label, active: true }]);
    setDraft('');
  };

  const move = (from, to) => {
    if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) return;
    const next = items.slice();
    const [row] = next.splice(from, 1);
    next.splice(to, 0, row);
    onChange(next);
  };

  const toggleActive = (index) => {
    const row = items[index];
    if (!row) return;
    if (row.active && activeCount < 2) return;
    onChange(items.map((item, itemIndex) => (
      itemIndex === index ? { ...item, active: !item.active } : item
    )));
  };

  const removeAt = (index) => {
    const row = items[index];
    if (!row || items.length < 2) return;
    if (row.active && activeCount < 2) return;
    onChange(items.filter((_, itemIndex) => itemIndex !== index));
  };

  return (
    <article className="org-values__card">
      <header className="org-values__head">
        <div>
          <h3>{title}</h3>
          <p>{note}</p>
        </div>
        <span className="org-values__count" aria-label={`${activeCount} active of ${items.length}`}>
          {String(activeCount).padStart(2, '0')}
        </span>
      </header>
      <ol className="org-values__list">
        {items.map((item, index) => (
          <li
            key={`${item.value}-${index}`}
            className={[
              item.active ? '' : 'is-inactive',
              dragIndex === index ? 'is-dragging' : '',
              overIndex === index && dragIndex !== index ? 'is-drop' : ''
            ].filter(Boolean).join(' ')}
            onDragOver={(event) => {
              event.preventDefault();
              event.dataTransfer.dropEffect = 'move';
              if (overIndex !== index) setOverIndex(index);
            }}
            onDragLeave={() => {
              if (overIndex === index) setOverIndex(null);
            }}
            onDrop={(event) => {
              event.preventDefault();
              const from = dragIndex == null ? Number(event.dataTransfer.getData('text/plain')) : dragIndex;
              move(from, index);
              setDragIndex(null);
              setOverIndex(null);
            }}
          >
            <span
              className="org-values__handle"
              title="Drag to reorder"
              aria-label={`Reorder ${item.value}`}
              draggable
              onDragStart={(event) => {
                setDragIndex(index);
                event.dataTransfer.effectAllowed = 'move';
                try { event.dataTransfer.setData('text/plain', String(index)); } catch (_) { /* ignore */ }
              }}
              onDragEnd={() => {
                setDragIndex(null);
                setOverIndex(null);
              }}
            >
              ⋮⋮
            </span>
            <span className="org-values__index">{String(index + 1).padStart(2, '0')}</span>
            <span className="org-values__label">{item.value}</span>
            <button
              type="button"
              className={`org-values__state${item.active ? ' is-on' : ''}`}
              aria-pressed={item.active}
              aria-label={item.active ? `Mark ${item.value} inactive` : `Mark ${item.value} active`}
              disabled={item.active && activeCount < 2}
              onClick={() => toggleActive(index)}
            >
              {item.active ? <BiCheck /> : null}
              {item.active ? 'On' : 'Off'}
            </button>
            <button
              type="button"
              className="org-values__remove"
              aria-label={`Remove ${item.value}`}
              disabled={items.length < 2 || (item.active && activeCount < 2)}
              onClick={() => removeAt(index)}
            >
              <BiX />
            </button>
          </li>
        ))}
      </ol>
      <div className="org-values__add">
        <input
          value={draft}
          maxLength={60}
          placeholder={full ? 'This list is full' : 'Add a value'}
          aria-label={`Add a ${title.toLowerCase()} value`}
          disabled={full}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commit();
            }
          }}
        />
        <button type="button" onClick={commit} disabled={full || !draft.trim()}>
          <BiPlus />
          Add
        </button>
      </div>
    </article>
  );
};

const OrganisationSettingsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [pack, setPack] = useState(null);
  const [form, setForm] = useState(null);
  const [message, setMessage] = useState(null);
  const [saving, setSaving] = useState(false);
  useDocumentTitle('Organisation');

  useEffect(() => {
    platformAPI.getOrganisation({ usage: '1' })
      .then((response) => {
        const data = response.data?.data;
        setPack(data);
        setForm(data?.settings || null);
      })
      .catch((error) => setMessage({ variant: 'danger', text: error.response?.data?.message || 'Unable to load organisation settings.' }));
  }, []);

  const setField = (name) => (event) => {
    setForm((current) => ({ ...current, [name]: event.target.value }));
  };

  const toggleCurrency = (listName, code) => {
    setForm((current) => {
      const has = current[listName].includes(code);
      let customerCurrencies = current.customerCurrencies.slice();
      let tenderCurrencies = current.tenderCurrencies.slice();
      if (listName === 'customerCurrencies') {
        if (has && customerCurrencies.length < 2) return current;
        customerCurrencies = has ? customerCurrencies.filter((item) => item !== code) : [...customerCurrencies, code];
        if (!has && !tenderCurrencies.includes(code)) tenderCurrencies = [...tenderCurrencies, code];
      } else {
        if (has && tenderCurrencies.length < 2) return current;
        if (has && customerCurrencies.length === 1 && customerCurrencies[0] === code) return current;
        tenderCurrencies = has ? tenderCurrencies.filter((item) => item !== code) : [...tenderCurrencies, code];
        if (has) customerCurrencies = customerCurrencies.filter((item) => item !== code);
      }
      const currency = customerCurrencies.includes(current.currency) ? current.currency : (customerCurrencies[0] || current.currency);
      return { ...current, customerCurrencies, tenderCurrencies, currency };
    });
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const response = await platformAPI.updateOrganisation({
        ...form,
        financialYearStartMonth: Number(form.financialYearStartMonth),
        passwordMinLength: Number(form.passwordMinLength),
        uploadMaxMb: Number(form.uploadMaxMb),
        auditRetentionDays: Number(form.auditRetentionDays)
      });
      const data = response.data?.data;
      setPack(data);
      setForm(data?.settings || form);
      setWorkspaceDateFormat(data?.settings?.dateFormat);
      applyBrandColours(data?.settings?.workspace?.primaryColor, data?.settings?.workspace?.secondaryColor);
      clearOrganisationSettings();
      setMessage({
        variant: data?.notice ? 'warning' : 'success',
        text: data?.notice || 'Organisation settings saved. The login page, the shell logo, and dates already on screen use the saved values after a refresh.'
      });
    } catch (error) {
      setMessage({ variant: 'danger', text: error.response?.data?.message || 'Unable to save organisation settings.' });
    } finally {
      setSaving(false);
    }
  };

  if (!form || !pack) return message ? <Alert variant="danger">{message.text}</Alert> : <Spinner animation="border" size="sm" />;

  return (
    <div className="cust-page">
      <form className="cust-record" onSubmit={save}>
        <header className="cust-mast">
          <div>
            <button type="button" className="cust-page__back" onClick={() => navigate(settingsBase(location.pathname))}>
              <BiArrowBack />
              Settings
            </button>
            <h1>Organisation</h1>
            <p className="text-muted">Currency, calendar, workspace identity, and the rules that apply after company setup.</p>
          </div>
          <Button size="sm" type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </header>
        {message ? <Alert variant={message.variant}>{message.text}</Alert> : null}

        <section className="cust-section">
          <header className="cust-section__head">
            <span>01</span>
            <div>
              <h2>Money</h2>
              <p>Choose the organisation currency, then the currencies customers and tenders may use. A customer currency is also offered on tenders. Estimates and rates stay in hundredths. For INR that unit is a paisa. Amounts already saved are not converted.</p>
            </div>
          </header>
          <div className="org-values org-money">
            <CurrencyBoard
              title="Organisation"
              note="The currency new records start with. It must also be available to customers."
              codes={pack.currencies.filter((row) => form.customerCurrencies.includes(row.code))}
              mode="default"
              current={form.currency}
              selected={form.customerCurrencies}
              onChoose={(code) => setForm((current) => ({ ...current, currency: code }))}
            />
            <CurrencyBoard
              title="Customers"
              note="Turning a currency on here also offers it on tenders."
              codes={pack.currencies}
              selected={form.customerCurrencies}
              onToggle={(code) => toggleCurrency('customerCurrencies', code)}
            />
            <CurrencyBoard
              title="Tenders"
              note="Tenders and instruments. Turning one off removes it from customers too."
              codes={pack.currencies}
              selected={form.tenderCurrencies}
              onToggle={(code) => toggleCurrency('tenderCurrencies', code)}
            />
          </div>
        </section>

        <section className="cust-section">
          <header className="cust-section__head">
            <span>02</span>
            <div>
              <h2>Calendar</h2>
              <p>These replace the values captured during company setup. The row marked Set is the one in use. Screens stay in English.</p>
            </div>
          </header>
          <div className="org-values org-money">
            <ChoiceBoard
              title="Time zone"
              note="Used for submission times and the working day."
              options={pack.timezones.map((zone) => ({ code: zone, label: zone }))}
              current={form.timezone}
              onChoose={(code) => setForm((current) => ({ ...current, timezone: code }))}
            />
            <ChoiceBoard
              title="Date style"
              note="How dates are written on the main lists."
              options={pack.dateFormats.map((item) => ({ code: item, label: item }))}
              current={form.dateFormat}
              onChoose={(code) => setForm((current) => ({ ...current, dateFormat: code }))}
            />
            <ChoiceBoard
              title="Language"
              note="The workspace stays in this language."
              options={pack.languages}
              current={form.language}
              onChoose={(code) => setForm((current) => ({ ...current, language: code }))}
            />
            <ChoiceBoard
              title="Country"
              note="The country stored for the organisation."
              options={pack.countries.map((item) => ({ code: item, label: item }))}
              current={form.country}
              onChoose={(code) => setForm((current) => ({ ...current, country: code }))}
            />
            <ChoiceBoard
              title="Financial year"
              note="The month the financial year starts."
              options={pack.months.map((name, index) => ({ code: String(index + 1), label: name }))}
              current={form.financialYearStartMonth}
              onChoose={(code) => setForm((current) => ({ ...current, financialYearStartMonth: Number(code) }))}
            />
          </div>
        </section>

        <section className="cust-section">
          <header className="cust-section__head">
            <span>03</span>
            <div>
              <h2>Rules</h2>
              <p>The figure on each card is the value in force. A shorter activity-log period removes older rows, and it cannot go below 30 days.</p>
            </div>
          </header>
          <div className="org-values">
            <RuleCard
              title="Password"
              note="The shortest password a new user or a reset may use."
              value={form.passwordMinLength}
              min="6"
              max="32"
              unit="characters"
              onChange={(value) => setForm((current) => ({ ...current, passwordMinLength: value }))}
            />
            <RuleCard
              title="Document"
              note="A file larger than this is refused."
              value={form.uploadMaxMb}
              min="1"
              max="200"
              unit="MB"
              onChange={(value) => setForm((current) => ({ ...current, uploadMaxMb: value }))}
            />
            <RuleCard
              title="Activity log"
              note="How long sign-in and change rows are kept."
              value={form.auditRetentionDays}
              min="30"
              max="3650"
              unit="days"
              onChange={(value) => setForm((current) => ({ ...current, auditRetentionDays: value }))}
            />
          </div>
        </section>

        <section className="cust-section">
          <header className="cust-section__head">
            <span>04</span>
            <div>
              <h2>Personnel types</h2>
              <p>Employee and Consultant are the starting types. Rename a row in place, or add another. A person already saved keeps a type that is later removed.</p>
            </div>
          </header>
          <div className="org-values">
            <PersonnelBoard
              types={form.personnelTypes}
              onChange={(personnelTypes) => setForm((current) => ({ ...current, personnelTypes }))}
            />
          </div>
        </section>

        <section className="cust-section">
          <header className="cust-section__head">
            <span>05</span>
            <div>
              <h2>Plan limits</h2>
              <p>New users, new tenders, and new documents stop when these numbers are reached. Records already saved stay.</p>
            </div>
          </header>
          <div className="cust-fields">
            <Field label="Users" wide="4">
              <Form.Control type="number" min="1" max="10000" step="1" value={form.maxUsers} onChange={setField('maxUsers')} />
              {pack.usage ? <p className="text-muted small mb-0">{pack.usage.users} users are already on the roster.</p> : null}
            </Field>
            <Field label="Tenders" wide="4">
              <Form.Control type="number" min="1" max="100000" step="1" value={form.maxTenders} onChange={setField('maxTenders')} />
              {pack.usage ? <p className="text-muted small mb-0">{pack.usage.tenders} tenders are already saved.</p> : null}
            </Field>
            <Field label="Document storage (MB)" wide="4">
              <Form.Control type="number" min="1" max="1000000" step="1" value={form.maxStorageMb} onChange={setField('maxStorageMb')} />
              {pack.usage ? <p className="text-muted small mb-0">{pack.usage.storageMb} MB of documents are already stored.</p> : null}
            </Field>
          </div>
        </section>

        <section className="cust-section">
          <header className="cust-section__head">
            <span>06</span>
            <div>
              <h2>Import values</h2>
              <p>Each card is one list the masters and the import accept. Drag a row to change the order used on forms. Mark a value Off to hide it from new choices without deleting it. Only active values appear in dropdowns. Type a value and press Enter. Excel column headings stay fixed, so a filled sheet still uploads.</p>
            </div>
          </header>
          <div className="org-values">
            {IMPORT_BOARDS.map(([key, title, note]) => (
              <ValueBoard
                key={key}
                title={title}
                note={note}
                values={form.importLists?.[key]}
                onChange={(next) => setForm((current) => ({
                  ...current,
                  importLists: { ...current.importLists, [key]: next }
                }))}
              />
            ))}
          </div>
        </section>

        <section className="cust-section">
          <header className="cust-section__head">
            <span>07</span>
            <div>
              <h2>Workspace</h2>
              <p>The short name, legal name, colours, logo, tagline, and login line. The letterhead is printed on the technical-response Word file. The help note appears at the top of Help. Leave that note blank to keep the guide as it is.</p>
            </div>
          </header>
          <div className="org-values">
            <IdentityCard
              title="Short name"
              note="Shown in the shell and on the browser tab."
              value={form.workspace?.displayName}
              max={80}
              onChange={(value) => setForm((current) => ({ ...current, workspace: { ...current.workspace, displayName: value } }))}
            />
            <IdentityCard
              title="Legal name"
              note="The registered name. It is the starting letterhead."
              value={form.workspace?.legalName}
              max={120}
              onChange={(value) => setForm((current) => ({ ...current, workspace: { ...current.workspace, legalName: value } }))}
            />
            <IdentityCard
              title="Login line"
              note="The heading on the sign-in page."
              value={form.workspace?.loginLine}
              max={120}
              onChange={(value) => setForm((current) => ({ ...current, workspace: { ...current.workspace, loginLine: value } }))}
            />
            <IdentityCard
              title="Tagline"
              note="The line under the login heading."
              value={form.workspace?.tagline}
              max={160}
              onChange={(value) => setForm((current) => ({ ...current, workspace: { ...current.workspace, tagline: value } }))}
            />
            <ColourCard
              primary={form.workspace?.primaryColor}
              secondary={form.workspace?.secondaryColor}
              onChange={(name, value) => setForm((current) => ({ ...current, workspace: { ...current.workspace, [name]: value } }))}
            />
            <LogoCard
              logoUrl={form.workspace?.logoUrl}
              onChange={(logoUrl) => setForm((current) => ({ ...current, workspace: { ...current.workspace, logoUrl } }))}
            />
            <IdentityCard
              title="Letterhead"
              note="Printed at the top of the technical-response Word file. Use a new line for each extra line."
              value={form.workspace?.letterhead}
              max={500}
              multiline
              wide
              onChange={(value) => setForm((current) => ({ ...current, workspace: { ...current.workspace, letterhead: value } }))}
            />
            <IdentityCard
              title="Help note"
              note="Shown at the top of Help on every page. Blank leaves the guide unchanged."
              value={form.workspace?.helpText}
              max={400}
              multiline
              wide
              onChange={(value) => setForm((current) => ({ ...current, workspace: { ...current.workspace, helpText: value } }))}
            />
          </div>
        </section>
        <TurnoverSection />
        <WorkPackageSettings />
      </form>
    </div>
  );
};

export default OrganisationSettingsPage;
